import { ioOption } from 'fp-ts';
import { pipe } from 'fp-ts/function';
import { QueryClient } from 'react-query';
import { UserMetadataQueryResponse, USER_METADATA_API } from '../../hooks/useUserMetadataQuery';

const createQueryKey = (readableUserKey: string) => [
  USER_METADATA_API.replace(':userKey', encodeURIComponent(readableUserKey)),
  undefined,
];

// Gets metadata cached in query client
export const getMetadata =
  (queryClient: QueryClient) =>
  (userKey: string): UserMetadataQueryResponse | undefined => {
    return pipe(userKey, createQueryKey, (k) =>
      queryClient.getQueryData<UserMetadataQueryResponse>(k),
    );
  };

// Incrementally mutate metadata using a passed in function
export const mergeMetadata =
  (f: (response: UserMetadataQueryResponse) => Partial<UserMetadataQueryResponse>) =>
  (response: UserMetadataQueryResponse): UserMetadataQueryResponse => ({
    ...response,
    ...f(response),
  });

// Update metadata cached in query client
export const updateMetadata =
  (userKey: string, queryClient: QueryClient) => (next: UserMetadataQueryResponse) => {
    return pipe(
      userKey,
      createQueryKey,
      ioOption.of, // convert to an IO-Option so we can trigger side effect with tapIO
      ioOption.tap((k) =>
        ioOption.of(queryClient.setQueryData<UserMetadataQueryResponse>(k, next)),
      ),
    );
  };
